import React, { useState, useEffect } from "react";
import { signInWithPopup, GoogleAuthProvider, signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import "../styles/AdminLogin.css";

interface LoginError {
  message: string;
  code?: string;
}

export const AdminLogin: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<LoginError | null>(null);
  const [user, setUser] = useState<any>(null);
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        // User is logged in, check if admin
        const idTokenResult = await currentUser.getIdTokenResult();
        const isAdmin = idTokenResult.claims.admin === true;

        if (isAdmin) {
          navigate("/admin/");
        } else {
          // Non-admin, offer logout
          setUser(currentUser);
        }
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const provider = new GoogleAuthProvider();
      // Request admin and profile scopes
      provider.addScope("profile");
      provider.addScope("email");

      const result = await signInWithPopup(auth, provider);
      const idTokenResult = await result.user.getIdTokenResult();
      const isAdmin = idTokenResult.claims.admin === true;

      if (!isAdmin) {
        // Sign out non-admin users
        await signOut(auth);
        setError({
          message:
            "管理者アカウントのみアクセスできます。管理者権限を持つ Google アカウントでログインしてください。",
          code: "NOT_ADMIN",
        });
        return;
      }

      // Admin login successful
      navigate("/admin");
    } catch (err: any) {
      let errorMessage = "ログインに失敗しました";

      if (err.code === "auth/popup-closed-by-user") {
        errorMessage = "ログインキャンセルされました";
      } else if (err.code === "auth/popup-blocked") {
        errorMessage =
          "ポップアップがブロックされています。ブラウザの設定を確認してください";
      } else if (err.code === "auth/cancelled-popup-request") {
        errorMessage = "ログイン要求がキャンセルされました";
      }

      setError({
        message: errorMessage,
        code: err.code,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setError(null);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-box">
        <h1 className="admin-login-title">管理者ログイン</h1>
        <p className="admin-login-subtitle">イラストコミッション管理画面</p>

        {user && !error ? (
          <div className="user-info">
            <div className="user-avatar">
              {user.photoURL ? (
                <img src={user.photoURL} alt={user.displayName} />
              ) : (
                <div className="avatar-placeholder">
                  {user.displayName?.charAt(0)}
                </div>
              )}
            </div>
            <div className="user-details">
              <p className="user-name">{user.displayName}</p>
              <p className="user-email">{user.email}</p>
              <p className="user-status">
                このアカウントは管理者権限がありません
              </p>
            </div>
            <button onClick={handleLogout} className="btn-logout-small">
              別のアカウントで試す
            </button>
          </div>
        ) : (
          <>
            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error.message}
              </div>
            )}

            <button
              onClick={handleGoogleLogin}
              className="btn-google-login"
              disabled={loading}
            >
              <svg className="google-icon" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {loading ? "ログイン中..." : "Google でログイン"}
            </button>
          </>
        )}

        <div className="admin-login-footer">
          <p className="footer-text">
            このページは管理者専用です。
            <br />
            Google OAuth で認証後、管理者カスタムクレームを確認して
            <br />
            アクセス権を検証します。
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
